import { useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from "react";
import { Plus, RefreshCw, Trash2, UploadCloud } from "lucide-react";
import clsx from "clsx";
import { ActionModal, Button, Field, Icon, Picker, DialogCancel } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useReplaceTracks, useAddTrackVariant, useRemoveTrackVariant, useRefreshSpotifyLabel, useCheckSpotifyTrackName } from "../../app/hooks/useRelease.js";
import { useUploadMedia } from "../../app/hooks/useMedia.js";
import { formatFileSize } from "../../app/hooks/inboxFormat.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { Modal } from "./Modal.js";
import {
  MASTER_ROLE,
  NO_ROLE,
  CUSTOM_VARIANT_KEY,
  SONG_FILE_ROLE_OPTIONS,
  labelForRole,
  roleForVariantLabel,
  suggestRoleFromFilename,
} from "./releaseSongFiles.js";
import styles from "./AssetEditorModal.module.scss";
import own from "./SongEditorModal.module.scss";

type TrackDto = components["schemas"]["TrackDto"];
type TrackInput = components["schemas"]["TrackInput"];
type ReleaseTracksRead = components["schemas"]["ReleaseTracksRead"];

// A song is a NAME + a table of FILES (ART-218). Drop many at once; each file uploads (multipart for the large
// ones) one at a time and COMMITS as it lands — master → the track's masterMediaId, a variant → the add-variant
// route. A per-row dropdown assigns the role (auto-suggested from the filename, overridable); a song has exactly
// ONE master (last pick wins, the previous master drops to a Custom alternate so its bytes aren't lost). No
// staging store: the queue is in-memory and every backend op is serialized so the release version never races.

export interface SongEditorModalProps {
  releaseId: string;
  /** The composed aggregate version — the tracks replace REQUIRES If-Match. */
  version: number;
  /** The current tracks, carried forward in the full-set replace so existing songs aren't dropped. */
  existingTracks: TrackDto[];
  /** When set, edit this existing track in place; else a new song is created lazily on the first committed file. */
  editTrack?: TrackDto | null;
  onClose: () => void;
}

// "idle" = dropped but awaiting a role (empty dropdown) — not uploaded, not committed, scrapped on close.
type RowStatus = "idle" | "queued" | "uploading" | "done" | "error";

interface FileRow {
  id: string;
  file?: File; // absent for rows loaded from an existing track
  name: string;
  size?: number;
  role: string; // NO_ROLE (undecided) | MASTER_ROLE | variant-kind key | CUSTOM_VARIANT_KEY
  customLabel?: string;
  status: RowStatus;
  progress?: number;
  mediaId?: string;
  variantId?: string; // set once committed as a variant
  master?: boolean; // committed as the track master
  error?: string;
}

let rowSeq = 0;
const nextRowId = () => `row-${++rowSeq}`;

function toInput(t: TrackDto): TrackInput {
  return {
    trackId: t.trackId ?? null,
    title: t.title ?? "",
    isrc: t.isrc ?? null,
    durationMs: t.durationMs ?? null,
    masterMediaId: t.masterMediaId ?? null,
    spotifyTrackId: t.spotifyTrackId ?? null,
    songstatsTrackId: t.songstatsTrackId ?? null,
  };
}

function initialRows(editTrack: TrackDto | null | undefined): FileRow[] {
  if (!editTrack) return [];
  const rows: FileRow[] = [];
  if (editTrack.masterMediaId) {
    rows.push({
      id: nextRowId(),
      name: editTrack.masterFileName ?? "Master file",
      size: editTrack.masterSizeBytes ?? undefined,
      role: MASTER_ROLE,
      status: "done",
      mediaId: editTrack.masterMediaId,
      master: true,
    });
  }
  for (const v of editTrack.variants ?? []) {
    const role = roleForVariantLabel(v.label ?? "");
    rows.push({
      id: nextRowId(),
      name: v.fileName ?? "File",
      size: v.sizeBytes ?? undefined,
      role,
      customLabel: role === CUSTOM_VARIANT_KEY ? (v.label ?? undefined) : undefined,
      status: "done",
      mediaId: v.mediaId,
      variantId: v.variantId,
    });
  }
  return rows;
}

export function SongEditorModal({ releaseId, version, existingTracks, editTrack, onClose }: SongEditorModalProps) {
  const { uploadMedia } = useUploadMedia();
  const replace = useReplaceTracks(releaseId, { silent: true });
  const addVariant = useAddTrackVariant(releaseId, { silent: true });
  const removeVariant = useRemoveTrackVariant(releaseId, { silent: true });
  const refreshLabel = useRefreshSpotifyLabel(releaseId);
  const checkName = useCheckSpotifyTrackName();

  const editing = editTrack != null;
  const [title, setTitle] = useState(editTrack?.title ?? "");
  // ART-219: the song's Spotify link (saved on Done, stored as the bare track id) and the read-only name Spotify
  // has for it — for checking the link names the song meant. The label belongs to the SAVED id; while the field
  // holds something else, the line says the name is checked on save instead.
  const [spotifyLink, setSpotifyLink] = useState(editTrack?.spotifyTrackId ?? "");
  const [spotifyLabel, setSpotifyLabel] = useState<string | null>(editTrack?.spotifyLabel ?? null);
  // "Check Spotify ID": the name Spotify has for an UNSAVED link, and which link it was checked for — typing again
  // makes it stale, so it only shows while the field still holds that exact link.
  const [checked, setChecked] = useState<{ link: string; name: string | null } | null>(null);
  const [rows, setRowsState] = useState<FileRow[]>(() => initialRows(editTrack));
  const [error, setError] = useState<string | null>(null);
  const [savingTitle, setSavingTitle] = useState(false);
  const [closing, setClosing] = useState(false); // rolling back on cancel
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Refs the async queue reads (state would be stale inside a running op).
  const rowsRef = useRef(rows);
  const titleRef = useRef(title);
  const versionRef = useRef(version);
  const trackIdRef = useRef<string | null>(editTrack?.trackId ?? null);
  const tracksRef = useRef<TrackInput[]>(existingTracks.map(toInput));
  const abortsRef = useRef(new Map<string, AbortController>());
  // Every backend mutation runs through this chain, so tracks-PUT / add-variant / remove-variant never overlap
  // and the release version stays consistent.
  const opChain = useRef<Promise<unknown>>(Promise.resolve());

  // ── Cancel-rollback journal: commit-as-you-go means Cancel has to UNDO what this session wrote. ───────────
  // Add mode: if we created the track, dropping it on cancel cascades its master + variants away — one op.
  const createdTrackIdRef = useRef<string | null>(null);
  // Edit mode: variants we ADDED this session (removed on cancel) and whether we touched the master (restored).
  const sessionVariantIdsRef = useRef<Set<string>>(new Set());
  const masterUndoRef = useRef<{ touched: boolean; original: string | null }>({ touched: false, original: editTrack?.masterMediaId ?? null });
  // Done was pressed → keep everything; any other close (X / Escape / Cancel) rolls back.
  const committedRef = useRef(false);

  function setRows(next: FileRow[] | ((prev: FileRow[]) => FileRow[])) {
    setRowsState((prev) => {
      const value = typeof next === "function" ? (next as (p: FileRow[]) => FileRow[])(prev) : next;
      rowsRef.current = value;
      return value;
    });
  }
  function updateRow(id: string, patch: Partial<FileRow>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }
  const rowById = (id: string) => rowsRef.current.find((r) => r.id === id);

  function runExclusive<T>(fn: () => Promise<T>): Promise<T> {
    const next = opChain.current.then(fn, fn);
    opChain.current = next.then(() => undefined, () => undefined);
    return next as Promise<T>;
  }

  function applyResult(res: ReleaseTracksRead) {
    versionRef.current = res.version ?? versionRef.current;
    if (res.tracks) tracksRef.current = res.tracks.map(toInput);
  }

  // ── Backend primitives (all called inside runExclusive) ────────────────────────────────────────────────
  async function ensureTrack(): Promise<string> {
    if (trackIdRef.current) return trackIdRef.current;
    const res = await replace.mutateAsync({
      version: versionRef.current,
      tracks: [
        ...tracksRef.current,
        { trackId: null, title: titleRef.current.trim() || "Untitled", isrc: null, durationMs: null, masterMediaId: null, spotifyTrackId: null, songstatsTrackId: null },
      ],
    });
    applyResult(res);
    const created = res.tracks?.find((t) => t.trackId && !existingTracks.some((e) => e.trackId === t.trackId));
    trackIdRef.current = created?.trackId ?? null;
    if (!trackIdRef.current) throw new Error("Could not create the song.");
    createdTrackIdRef.current = trackIdRef.current; // this session minted it → cancel drops it whole
    return trackIdRef.current;
  }

  async function setTrackMaster(mediaId: string | null) {
    const trackId = await ensureTrack();
    if (!masterUndoRef.current.touched) masterUndoRef.current.touched = true; // remembers the original for cancel
    const res = await replace.mutateAsync({
      version: versionRef.current,
      tracks: tracksRef.current.map((t) => (t.trackId === trackId ? { ...t, masterMediaId: mediaId } : t)),
    });
    applyResult(res);
  }

  async function addVariantFor(mediaId: string, label: string): Promise<string | undefined> {
    const trackId = await ensureTrack();
    const res = await addVariant.mutateAsync({ trackId, body: { label, mediaId } });
    applyResult(res);
    const vId = res.tracks?.find((t) => t.trackId === trackId)?.variants?.find((v) => v.mediaId === mediaId)?.variantId ?? undefined;
    if (vId) sessionVariantIdsRef.current.add(vId); // added this session → removed on cancel
    return vId;
  }

  async function removeVariantFor(variantId: string) {
    if (!trackIdRef.current) return;
    const res = await removeVariant.mutateAsync({ trackId: trackIdRef.current, variantId });
    applyResult(res);
    sessionVariantIdsRef.current.delete(variantId); // gone now — don't try to remove it again on cancel
  }

  /** Move any OTHER master off the master slot (last-wins). A committed master's bytes become a Custom alternate. */
  async function demoteOtherMasters(exceptId: string) {
    for (const r of [...rowsRef.current]) {
      if (r.id === exceptId || r.role !== MASTER_ROLE) continue;
      updateRow(r.id, { role: CUSTOM_VARIANT_KEY, customLabel: "Alternate" });
      if (r.master && r.mediaId) {
        const vId = await addVariantFor(r.mediaId, "Alternate");
        updateRow(r.id, { master: false, variantId: vId });
      }
    }
  }

  /** First commit of a freshly uploaded file, per its current role. */
  async function commit(id: string, mediaId: string) {
    const role = rowById(id)?.role ?? MASTER_ROLE;
    if (role === MASTER_ROLE) {
      await demoteOtherMasters(id);
      await setTrackMaster(mediaId);
      updateRow(id, { master: true, variantId: undefined });
    } else {
      const vId = await addVariantFor(mediaId, labelForRole(role, rowById(id)?.customLabel));
      updateRow(id, { variantId: vId, master: false });
    }
  }

  /** Re-attach an already-committed row to a new role (dropdown change on a done row). */
  async function reattach(id: string, newRole: string) {
    const row = rowById(id);
    if (!row?.mediaId) return;
    // Tear down the old attachment.
    if (row.master) await setTrackMaster(null);
    else if (row.variantId) await removeVariantFor(row.variantId);
    // Build the new one.
    if (newRole === MASTER_ROLE) {
      await demoteOtherMasters(id);
      await setTrackMaster(row.mediaId);
      updateRow(id, { master: true, variantId: undefined });
    } else {
      const vId = await addVariantFor(row.mediaId, labelForRole(newRole, rowById(id)?.customLabel));
      updateRow(id, { master: false, variantId: vId });
    }
  }

  // ── Queue processing (one file at a time via the op chain) ─────────────────────────────────────────────
  function enqueue(id: string) {
    void runExclusive(async () => {
      const row = rowById(id);
      if (!row?.file || row.status === "done") return;
      const ac = new AbortController();
      abortsRef.current.set(id, ac);
      updateRow(id, { status: "uploading", progress: 0, error: undefined });
      try {
        const media = await uploadMedia(row.file, {
          // Always AUDIO-kind: an .mp4 reports video/mp4, but here it is a song file, so it must take the audio
          // upload cap, skip video renditions, and stay streamable by the share player (which serves kind="audio").
          kind: "audio",
          purpose: rowById(id)?.role === MASTER_ROLE ? "release-master" : "release-variant",
          onProgress: (p) => updateRow(id, { progress: p }),
          signal: ac.signal,
        });
        updateRow(id, { mediaId: media.mediaId! });
        await commit(id, media.mediaId!);
        updateRow(id, { status: "done" });
      } catch (e) {
        if (ac.signal.aborted) return; // removed mid-upload — its row is already gone
        updateRow(id, { status: "error", error: errorMessage(e, "Upload failed") });
      } finally {
        abortsRef.current.delete(id);
      }
    });
  }

  function addFiles(files: File[]) {
    setError(null);
    // A filename keyword picks the role; the first unlabelled file claims the free master slot. Anything left —
    // no keyword AND the master already taken — gets NO_ROLE (an empty dropdown): we don't guess Custom, the
    // user decides, and the file waits (unuploaded) until they do.
    let masterTaken = rowsRef.current.some((r) => r.role === MASTER_ROLE);
    const additions: FileRow[] = files.map((file) => {
      let role = suggestRoleFromFilename(file.name);
      if (role === MASTER_ROLE) {
        if (masterTaken) role = NO_ROLE;
        else masterTaken = true;
      }
      const status: RowStatus = role === NO_ROLE ? "idle" : "queued";
      return { id: nextRowId(), file, name: file.name, size: file.size, role, status };
    });
    setRows((prev) => [...prev, ...additions]);
    for (const r of additions) if (r.role !== NO_ROLE) enqueue(r.id);
  }

  // ── The drop pane IS the control: the container accepts drops, browse opens only from its own affordances
  // (so a row's dropdown/remove never triggers the file picker — the reason it is a div, not a <label>). ─────
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // .mp4 is accepted too (ART-218): a song file delivered as MP4 reports video/mp4, so the audio/* test alone
  // silently dropped it.
  const audioAccept = /\.(wav|flac|mp3|aif|aiff|m4a|mp4|aac|ogg|atmosir|atmos)$/i;
  const isAudio = (f: File) => f.type.startsWith("audio/") || f.type === "video/mp4" || audioAccept.test(f.name);

  function browse() {
    inputRef.current?.click();
  }
  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.currentTarget.files ?? []);
    e.currentTarget.value = ""; // allow re-picking the same file
    const audio = files.filter(isAudio);
    if (audio.length) addFiles(audio);
  }
  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (savingTitle || closing) return;
    if (!dragging) setDragging(true);
  }
  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragging(false);
  }
  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (savingTitle || closing) return;
    const audio = Array.from(e.dataTransfer.files).filter(isAudio);
    if (audio.length) addFiles(audio);
  }

  function onRoleChange(id: string, role: string) {
    const row = rowById(id);
    if (!row || row.role === role) return;
    // Single master in the UI immediately; the backend demotion happens in commit/reattach. A picker never
    // offers NO_ROLE, so any incoming role here is a real choice.
    if (role === MASTER_ROLE) {
      for (const r of rowsRef.current) if (r.id !== id && r.role === MASTER_ROLE && r.status !== "done") updateRow(r.id, { role: NO_ROLE, status: r.status === "queued" ? "idle" : r.status });
    }
    updateRow(id, { role });
    if (row.status === "done") void runExclusive(() => reattach(id, role));
    else if (row.status === "idle") enqueue(id); // the file was waiting on a role — upload + commit it now
  }

  function onCustomLabelChange(id: string, customLabel: string) {
    updateRow(id, { customLabel });
    const row = rowById(id);
    if (row?.status === "done" && row.role === CUSTOM_VARIANT_KEY && row.variantId) {
      void runExclusive(async () => {
        await removeVariantFor(row.variantId!);
        const vId = await addVariantFor(row.mediaId!, labelForRole(CUSTOM_VARIANT_KEY, customLabel));
        updateRow(id, { variantId: vId });
      });
    }
  }

  function removeRow(id: string) {
    const row = rowById(id);
    if (!row) return;
    abortsRef.current.get(id)?.abort();
    setRows((prev) => prev.filter((r) => r.id !== id));
    if (row.status === "done") {
      void runExclusive(async () => {
        if (row.master) await setTrackMaster(null);
        else if (row.variantId) await removeVariantFor(row.variantId);
      });
    }
  }

  const anyBusy = rows.some((r) => r.status === "uploading" || r.status === "queued");

  async function done() {
    setSavingTitle(true);
    setError(null);
    try {
      const trimmed = title.trim();
      const link = spotifyLink.trim();
      await runExclusive(async () => {
        // A name (or a Spotify link) with no files yet — create the empty song so it isn't lost.
        if (!trackIdRef.current && (trimmed || link)) await ensureTrack();
        if (trackIdRef.current) {
          // Update the title and the Spotify link if either changed — one save. The server validates the link (a
          // non-track paste is a 400 shown below) and reads the song's Spotify name once for a new id.
          const current = tracksRef.current.find((t) => t.trackId === trackIdRef.current);
          if (current && ((current.title ?? "") !== trimmed || (current.spotifyTrackId ?? "") !== link)) {
            const res = await replace.mutateAsync({
              version: versionRef.current,
              tracks: tracksRef.current.map((t) =>
                t.trackId === trackIdRef.current ? { ...t, title: trimmed || "Untitled", spotifyTrackId: link || null } : t,
              ),
            });
            applyResult(res);
          }
        }
      });
      committedRef.current = true; // keep what we wrote; the close below must not roll it back
      onClose();
    } catch (e) {
      setError(errorMessage(e, "Couldn't save the song"));
    } finally {
      setSavingTitle(false);
    }
  }

  /**
   * Undo everything this session committed (commit-as-you-go means Cancel owes a rollback). Best-effort.
   * Add mode is total (the created track is dropped whole). Edit mode reverses this session's ADDITIONS — the
   * variants we added and the master we changed. The one residual, named rather than hidden: RE-ROLING a
   * pre-existing variant (reattach) and then cancelling does not restore that variant's original label, since
   * the original attachment was already torn down; the common cases (add song, add master, add a variant) roll
   * back cleanly.
   */
  async function rollback() {
    if (createdTrackIdRef.current) {
      // Add mode: drop the track we minted — the full-set replace cascades its master + variants away.
      const res = await replace.mutateAsync({
        version: versionRef.current,
        tracks: tracksRef.current.filter((t) => t.trackId !== createdTrackIdRef.current),
      });
      applyResult(res);
      return;
    }
    // Edit mode: pull the variants we added, then put the master back to where it started.
    for (const vId of [...sessionVariantIdsRef.current]) {
      try {
        await removeVariantFor(vId);
      } catch {
        /* already gone — nothing to undo */
      }
    }
    if (masterUndoRef.current.touched && trackIdRef.current) {
      const res = await replace.mutateAsync({
        version: versionRef.current,
        tracks: tracksRef.current.map((t) => (t.trackId === trackIdRef.current ? { ...t, masterMediaId: masterUndoRef.current.original } : t)),
      });
      applyResult(res);
    }
  }

  async function handleClose() {
    if (savingTitle || closing) return;
    for (const ac of abortsRef.current.values()) ac.abort(); // stop any in-flight uploads first
    if (committedRef.current) {
      onClose();
      return;
    }
    // Truly cancel: await the rollback so the parent's post-close refetch sees the clean state, not the
    // half-written one. It runs on the op chain, so it lands after any commit that was already in flight.
    setClosing(true);
    try {
      await runExclusive(rollback);
    } catch {
      /* best-effort — a failed rollback must never trap the user in the modal */
    } finally {
      onClose();
    }
  }

  /** Delete the whole song (edit mode) — a full-set replace dropping this track, carrying every other forward. */
  async function confirmDelete() {
    setDeleting(true);
    setError(null);
    try {
      await runExclusive(async () => {
        const id = trackIdRef.current;
        if (!id) return;
        const res = await replace.mutateAsync({
          version: versionRef.current,
          tracks: tracksRef.current.filter((t) => t.trackId !== id),
        });
        applyResult(res);
      });
      committedRef.current = true; // an intentional delete is never rolled back by the close
      onClose();
    } catch (e) {
      setError(errorMessage(e, "Couldn't delete the song"));
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  function retry(id: string): string {
    updateRow(id, { status: "queued", error: undefined });
    return id;
  }

  function statusLabel(row: FileRow): string {
    if (row.status === "idle") return " · Choose a role";
    if (row.status === "queued") return " · Queued";
    if (row.status === "uploading") return ` · Uploading ${Math.round((row.progress ?? 0) * 100)}%`;
    if (row.status === "error") return ` · ${row.error ?? "Failed"}`;
    return "";
  }

  /** The id the Spotify label belongs to — the SAVED link, which the label was read for. */
  const savedSpotifyId = (editTrack && tracksRef.current.find((t) => t.trackId === editTrack.trackId)?.spotifyTrackId) || "";

  async function refetchLabel() {
    const trackId = trackIdRef.current;
    if (!trackId) return;
    try {
      const res = await runExclusive(() => refreshLabel.mutateAsync({ trackId }));
      applyResult(res);
      setSpotifyLabel(res.tracks?.find((t) => t.trackId === trackId)?.spotifyLabel ?? null);
    } catch {
      /* toasted by the hook */
    }
  }

  async function checkLink() {
    const link = spotifyLink.trim();
    try {
      const r = await checkName.mutateAsync({ link });
      setChecked({ link, name: r.name ?? null });
    } catch {
      /* toasted by the hook */
    }
  }

  /** The line under the Spotify link: what Spotify calls the song, so the person can check it matches — for the
   *  SAVED link (refetchable), or, for one typed but not yet saved, on request ("Check Spotify ID"). */
  function spotifyHint() {
    const link = spotifyLink.trim();
    if (!link) return undefined;
    const saved = link === savedSpotifyId;
    if (!saved && checked?.link !== link) {
      return spotifyLine("Check Spotify ID", () => void checkLink(), checkName.isPending, "Check this link on Spotify");
    }
    const name = saved ? spotifyLabel : checked!.name;
    const text = name ? <>On Spotify: “{name}”</> : saved ? "Couldn't read the name from Spotify." : "Spotify doesn't know this track.";
    return saved
      ? spotifyLine(text, () => void refetchLabel(), refreshLabel.isPending, "Refetch the name from Spotify")
      : spotifyLine(text, () => void checkLink(), checkName.isPending, "Check this link on Spotify");
  }

  function spotifyLine(text: ReactNode, onClick: () => void, busy: boolean, label: string) {
    return (
      <span className={own.spotifyLabel}>
        <span>{text}</span>
        <button
          type="button"
          className={own.spotifyRefresh}
          onClick={onClick}
          disabled={busy || savingTitle}
          aria-label={label}
          aria-busy={busy || undefined}
        >
          <Icon icon={RefreshCw} size={16} />
        </button>
      </span>
    );
  }

  // keep refs in sync with render state used by ops
  titleRef.current = title;

  return (
    <Modal title={editing ? "Edit song" : "Add song"} onClose={handleClose}>
      <div className={styles.body}>
        <Field label="Name" placeholder="Song title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={savingTitle} />

        <Field
          label="Spotify link"
          placeholder="Paste the song's Spotify link"
          value={spotifyLink}
          onChange={(e) => setSpotifyLink(e.target.value)}
          disabled={savingTitle}
          hint={spotifyHint()}
        />

        <div className={styles.field}>
          <span className={styles.label}>Files</span>
          {/* The pane IS the control: a solid surface that accepts drops and populates itself. It is a <div>, not
              a <label>, so a row's dropdown/remove never opens the picker — browse is reached only from its own
              two explicit affordances (the empty-state button and the "Drop more" footer). */}
          <div
            className={own.pane}
            data-dragging={dragging || undefined}
            data-empty={rows.length === 0 || undefined}
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <input
              ref={inputRef}
              type="file"
              accept="audio/*,video/mp4,.mp4"
              multiple
              className={own.paneInput}
              aria-label="Add audio files"
              onChange={onInputChange}
            />
            {rows.length === 0 ? (
              <button type="button" className={own.paneEmpty} onClick={browse} disabled={savingTitle || closing}>
                <Icon icon={UploadCloud} size={22} />
                <span className={own.panePrompt}>Drop audio files, or browse</span>
              </button>
            ) : (
              <>
                <ul className={own.fileList}>
                  {rows.map((row) => (
                    <li key={row.id} className={own.fileRow} data-status={row.status}>
                      <span className={own.fileMain}>
                        <span className={own.fileName}>{row.name}</span>
                        <span className={own.fileMeta}>
                          {row.size != null ? formatFileSize(row.size) : ""}
                          {statusLabel(row)}
                        </span>
                      </span>
                      <span className={own.fileRole}>
                        <Picker
                          label="Role"
                          bare
                          triggerClassName={clsx(own.roleTrigger, row.role === NO_ROLE && own.roleTriggerEmpty)}
                          value={row.role}
                          placeholder="Choose a role"
                          onChange={(v) => onRoleChange(row.id, v)}
                          options={[...SONG_FILE_ROLE_OPTIONS]}
                          disabled={savingTitle || closing}
                        />
                        {row.role === CUSTOM_VARIANT_KEY && (
                          <Field
                            label="Custom label"
                            hideLabel
                            density="compact"
                            placeholder="Label"
                            value={row.customLabel ?? ""}
                            onChange={(e) => onCustomLabelChange(row.id, e.target.value)}
                            disabled={savingTitle}
                          />
                        )}
                      </span>
                      {row.status === "uploading" && (
                        <span className={own.fileBar} aria-hidden="true">
                          <span className={own.fileBarFill} style={{ width: `${Math.round((row.progress ?? 0) * 100)}%` }} />
                        </span>
                      )}
                      <span className={own.fileActions}>
                        {row.status === "error" && (
                          <Button size="sm" variant="ghost" onClick={() => enqueue(retry(row.id))}>
                            Retry
                          </Button>
                        )}
                        <button
                          type="button"
                          className={own.iconBtn}
                          aria-label={`Remove ${row.name}`}
                          onClick={() => removeRow(row.id)}
                        >
                          <Icon icon={Trash2} size={16} />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
                <button type="button" className={own.paneMore} onClick={browse} disabled={savingTitle || closing}>
                  <Icon icon={Plus} size={16} />
                  Drop more or browse
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          {editing && (
            <Button variant="ghost" tone="danger" size="sm" onClick={() => setConfirmingDelete(true)} disabled={savingTitle || closing || deleting}>
              Delete song
            </Button>
          )}
          <div className={styles.actionsRight}>
            <DialogCancel size="sm" loading={closing} disabled={savingTitle || deleting} />
            <Button size="sm" onClick={done} loading={savingTitle} disabled={anyBusy || closing || deleting}>
              Done
            </Button>
          </div>
        </div>
      </div>

      {confirmingDelete && (
        <ActionModal
          title="Delete song?"
          confirmLabel="Delete"
          confirmTone="danger"
          loading={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setConfirmingDelete(false)}
        >
          <p>Delete “{title.trim() || editTrack?.title || "this song"}” and all its files from this release?</p>
        </ActionModal>
      )}
    </Modal>
  );
}
